"use client";

import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import Link from "next/link";
import { useAuth, useUser } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { generateSlug } from "@/lib/slugUtils";

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_KEY!
);

interface PageParams {
  params: {
    username: string;
  };
}

export default function ProfileJournalPage({ params }: { params: Promise<{ username: string }> }) {
  const { userId } = useAuth();
  const { user } = useUser();
  const router = useRouter();
 
  const [journals, setJournals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [journalTitle, setJournalTitle] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    async function resolveParams() {
      const resolvedParams = await params;
      setUsername(resolvedParams.username);
    }
    resolveParams();
  }, [params]);

  useEffect(() => {
    if (username) {
      fetchUserAndJournals();
    }
  }, [username]);

  useEffect(() => {
    fetchUserAndJournals();
  }, [username]);

  const fetchUserAndJournals = async () => {
    try {
      // Fetch all journals and find those belonging to the username
      const { data: allJournals, error } = await supabase
        .from("journals")
        .select("*")
        .order("created_at", { ascending: false });

      if (error || !allJournals) {
        setLoading(false);
        return;
      }

      // Filter journals by finding the matching user
      const journalsWithUsers = await Promise.all(
        allJournals.map(async (journal) => {
          let userInfo = { username: "Anonymous", imageUrl: "" };
          let chapterCount = 0;

          try {
            const userRes = await fetch(`/api/clerk-user/?userId=${journal.user_id}`);
            userInfo = await userRes.json();
          } catch (e) {
            console.error(e);
          }

          try {
            const { data: chapters, error: chaptersError } = await supabase
              .from("journal_chapters")
              .select("id")
              .eq("journal_id", journal.id);

            if (!chaptersError && chapters) {
              chapterCount = chapters.length;
            }
          } catch (e) {
            console.error(e);
          }

          return { ...journal, userInfo, chapterCount };
        })
      );

      // Filter journals by matching username
      const userJournals = journalsWithUsers.filter(
        (journal) => journal.userInfo?.username?.toLowerCase() === username.toLowerCase()
      );

      // Set profile user info from the first journal's userInfo
      if (userJournals.length > 0) {
        setProfileUser(userJournals[0].userInfo);
      }

      setJournals(userJournals);
    } catch (e) {
      console.error(e);
    }

    setLoading(false);
  };

  const handleCreate = async () => {
    if (!userId || !user || !journalTitle.trim()) return;

    setIsCreating(true);
    
    // Create journal entry
    const { data: newJournal, error } = await supabase
      .from("journals")
      .insert([
        {
          user_id: userId,
          title: journalTitle,
        },
      ])
      .select()
      .single();

    setIsCreating(false);

    if (!error && newJournal) {
      // Generate slug from title and ID
      const slug = generateSlug(newJournal.title, newJournal.id);
      
      // Update the journal with the slug
      await supabase
        .from("journals")
        .update({ slug })
        .eq("id", newJournal.id);

      setShowModal(false);
      setJournalTitle("");
      router.refresh();
      fetchUserAndJournals();
    }
  };

  if (loading) {
    return <div className="p-8 bg-amber-50 text-gray-900 min-h-screen">Loading journals...</div>;
  }

  const isOwnProfile = user?.username?.toLowerCase() === username?.toLowerCase();

  return (
    <main className="bg-amber-50 min-h-screen text-black font-sans">
      <div className="max-w-5xl mx-auto p-8">
        <div className="flex justify-between items-center border-b border-amber-200 pb-6 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">
              @{username}'s Journals
            </h1>
            {profileUser && (
              <p className="text-gray-600 mt-2">
                {journals.length} {journals.length === 1 ? "journal" : "journals"}
              </p>
            )}
          </div>

          {isOwnProfile && (
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-2 bg-emerald-600 text-white font-medium rounded-md hover:bg-emerald-700 transition"
            >
              + Create Journal
            </button>
          )}
        </div>

        <div className="space-y-4">
          {journals.map((journal) => (
            <Link
              key={journal.id}
              href={`/journals/${journal.slug}`}
              className="block p-6 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md hover:border-indigo-300 transition group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 group-hover:text-indigo-700 transition mb-2">
                    {journal.title}
                  </h2>
                  {journal.description && (
                    <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                      {journal.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{journal.chapterCount} {journal.chapterCount === 1 ? "chapter" : "chapters"}</span>
                    <span>•</span>
                    <span>{new Date(journal.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="ml-4 text-indigo-600 font-semibold group-hover:text-indigo-800 transition">
                  →
                </div>
              </div>
            </Link>
          ))}
        </div>

        {journals.length === 0 && (
          <div className="text-center py-20 bg-white/50 rounded-3xl border-2 border-dashed border-amber-200 text-gray-400">
            <p className="text-xl italic">This user has no journals yet.</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl max-w-sm w-full shadow-2xl text-center">
            <h2 className="text-2xl font-bold mb-2">Create a New Journal</h2>
            <p className="text-gray-600 mb-6">
              Start a new journal with a title
            </p>
            
            <input
              type="text"
              value={journalTitle}
              onChange={(e) => setJournalTitle(e.target.value)}
              placeholder="Journal title..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-6 outline-none focus:ring-2 focus:ring-indigo-500"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCreate();
                }
              }}
            />
             
            <div className="flex flex-col gap-3">
              <button
                onClick={handleCreate}
                disabled={isCreating || !journalTitle.trim()}
                className="w-full py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? "Creating..." : "Create Journal"}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setJournalTitle("");
                }}
                className="w-full py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
